import React, { useState, useEffect } from 'react'
import {
  ArrowLeft,
  Save,
  RotateCcw,
  Layers,
  FolderOpen,
  Settings,
  DollarSign,
  Calendar,
  Users,
  CheckSquare,
  Plus,
  Tag,
  Palette,
  HelpCircle
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import projectCategoryMasterService from '../../services/projectCategoryMasterService'

const ProjectCategoryMasterForm = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('basicInfo')
  const [parentCategories, setParentCategories] = useState([])
  const [formData, setFormData] = useState({
    categoryCode: '',
    categoryName: '',
    categoryDescription: '',
    categoryType: 'Primary',
    parentCategory: '',
    categoryStatus: 'Active',
    categoryColor: '#222448',
    categoryIcon: 'folder',
    isDefault: false,
    sortOrder: 0,
    applicableProjectTypes: [],
    requiresApproval: false,
    budgetAllocation: {
      minBudget: 0,
      maxBudget: 0,
      defaultBudget: 0,
      currency: 'INR'
    },
    timelineSettings: {
      defaultDuration: 30,
      durationUnit: 'Days',
      allowExtension: true,
      maxExtensionDays: 30
    },
    resourceRequirements: {
      minTeamSize: 1,
      maxTeamSize: 10,
      requiredSkills: [],
      preferredRoles: []
    },
    qualityStandards: {
      qualityLevel: 'Standard',
      requiresTesting: true,
      requiresDocumentation: true,
      requiresCodeReview: true,
      complianceRequirements: []
    }
  })

  const [loading, setLoading] = useState(false)

  useEffect(() => {
    loadParentCategories()
  }, [])

  const loadParentCategories = async () => {
    try {
      const response = await projectCategoryMasterService.getParentCategories()
      if (response.success) {
        setParentCategories(response.data)
      }
    } catch (error) {
      console.error('Error loading parent categories:', error)
    }
  }

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.')
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }))
    }
  }

  const addArrayItem = (field, newItem) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], newItem || '']
    }))
  }

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }))
  }

  const validateForm = () => {
    if (!formData.categoryCode.trim()) {
      toast.error('Category Code is required')
      return false
    }
    if (!/^[A-Z0-9-]+$/.test(formData.categoryCode.trim())) {
      toast.error('Category Code must contain only uppercase letters, numbers, and hyphens')
      return false
    }
    if (!formData.categoryName.trim()) {
      toast.error('Category Name is required')
      return false
    }
    if (!formData.categoryDescription.trim()) {
      toast.error('Category Description is required')
      return false
    }
    if (formData.budgetAllocation.minBudget < 0) {
      toast.error('Minimum Budget cannot be negative')
      return false
    }
    if (formData.budgetAllocation.maxBudget < 0) {
      toast.error('Maximum Budget cannot be negative')
      return false
    }
    if (formData.budgetAllocation.minBudget > formData.budgetAllocation.maxBudget) {
      toast.error('Minimum Budget cannot be greater than Maximum Budget')
      return false
    }
    if (formData.timelineSettings.defaultDuration < 1) {
      toast.error('Default Duration must be at least 1')
      return false
    }
    if (formData.resourceRequirements.minTeamSize < 1) {
      toast.error('Minimum Team Size must be at least 1')
      return false
    }
    if (formData.resourceRequirements.minTeamSize > formData.resourceRequirements.maxTeamSize) {
      toast.error('Minimum Team Size cannot be greater than Maximum Team Size')
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    setLoading(true)
    try {
      const response = await projectCategoryMasterService.createProjectCategoryMaster(formData)

      if (response.success) {
        toast.success('Project Category Master created successfully!')
        navigate('/projects/project-category-master')
      } else {
        toast.error(response.message || 'Failed to create project category master')
      }
    } catch (error) {
      console.error('Error creating project category master:', error)
      if (error.response?.status === 400 && error.response.data?.message.includes('Category code already exists')) {
        toast.error('Category Code already exists. Please use a different code.')
      } else if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to perform this action.')
      } else {
        toast.error('Failed to create project category master. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      categoryCode: '',
      categoryName: '',
      categoryDescription: '',
      categoryType: 'Primary',
      parentCategory: '',
      categoryStatus: 'Active',
      categoryColor: '#222448',
      categoryIcon: 'folder',
      isDefault: false,
      sortOrder: 0,
      applicableProjectTypes: [],
      requiresApproval: false,
      budgetAllocation: {
        minBudget: 0,
        maxBudget: 0,
        defaultBudget: 0,
        currency: 'INR'
      },
      timelineSettings: {
        defaultDuration: 30,
        durationUnit: 'Days',
        allowExtension: true,
        maxExtensionDays: 30
      },
      resourceRequirements: {
        minTeamSize: 1,
        maxTeamSize: 10,
        requiredSkills: [],
        preferredRoles: []
      },
      qualityStandards: {
        qualityLevel: 'Standard',
        requiresTesting: true,
        requiresDocumentation: true,
        requiresCodeReview: true,
        complianceRequirements: []
      }
    })
    setActiveTab('basicInfo')
    toast.info('Form reset to default values')
  }

  const handleBack = () => {
    navigate('/projects/project-category-master')
  }

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Masters', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Project Category Master', path: '/projects/project-category-master', onClick: () => navigate('/projects/project-category-master') },
    { label: 'Create New', path: '/projects/project-category-master/new', isCurrent: true }
  ]

  const projectTypes = [
    { value: 'Internal', label: 'Internal' },
    { value: 'External', label: 'External' },
    { value: 'Support', label: 'Support' },
    { value: 'Development', label: 'Development' }
  ]

  const currencies = [
    'INR',
    'USD',
    'EUR',
    'GBP'
  ]

  const durationUnits = [
    'Days',
    'Weeks',
    'Months'
  ]

  const qualityLevels = [
    'Basic',
    'Standard',
    'Premium',
    'Enterprise'
  ]

  const categoryIcons = [
    'folder', 'globe', 'smartphone', 'database', 'code', 'server', 'layers', 'link',
    'wrench', 'users', 'bar-chart', 'shopping-cart', 'user-check', 'database', 'code'
  ]

  const helpContent = (
    <>
      <h3>What is Project Category Master?</h3>
      <p>Project Category Master lets you <strong>organize projects by categories</strong> with hierarchies and budgets. You can set category type, parent, status, color/icon, budget allocation, timeline settings, resource requirements, and quality standards.</p>
      <h3>Tabs</h3>
      <p><strong>Basic Info</strong> – Category code, name, description, type, parent category, status, color, icon, sort order, applicable project types.</p>
      <p><strong>Budget &amp; Timeline</strong> – Min/max/default budget, currency, default duration, extension rules.</p>
      <p><strong>Resources &amp; Quality</strong> – Team size limits, required skills, preferred roles, quality level, testing/documentation/code review and compliance.</p>
      <h3>How to use</h3>
      <p>Fill required fields (code, name, description). Use <strong>Save</strong> to create. Use <strong>Reset</strong> to clear or navigate back to cancel.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Project Category Master Entry</h1>
          <p>Create and manage project category information</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Project Category Master Entry</h2>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'basicInfo' ? 'active' : ''}`}
            onClick={() => setActiveTab('basicInfo')}
          >
            <Layers size={18} />
            <span>Basic Information</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'budget' ? 'active' : ''}`}
            onClick={() => setActiveTab('budget')}
          >
            <DollarSign size={18} />
            <span>Budget & Timeline</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'resources' ? 'active' : ''}`}
            onClick={() => setActiveTab('resources')}
          >
            <Users size={18} />
            <span>Resources & Quality</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <Settings size={18} />
            <span>Settings</span>
          </button>
        </div>

        <form className="form-content">

          {/* Basic Information Tab */}
          {activeTab === 'basicInfo' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Basic Information</h3>
              </div>

              {/* Row 1: Category Code, Category Name, Category Type, Status */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="categoryCode" className="form-label required">
                    Category Code
                  </label>
                  <input
                    id="categoryCode"
                    type="text"
                    value={formData.categoryCode}
                    onChange={(e) => handleInputChange('categoryCode', e.target.value)}
                    className="form-input"
                    placeholder="e.g., WEB-DEV, MOBILE-DEV"
                    maxLength={20}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="categoryName" className="form-label required">
                    Category Name
                  </label>
                  <input
                    id="categoryName"
                    type="text"
                    value={formData.categoryName}
                    onChange={(e) => handleInputChange('categoryName', e.target.value)}
                    className="form-input"
                    placeholder="e.g., Web Development"
                    maxLength={100}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="categoryType" className="form-label required">
                    Category Type
                  </label>
                  <select
                    id="categoryType"
                    value={formData.categoryType}
                    onChange={(e) => handleInputChange('categoryType', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Type</option>
                    <option value="Primary">Primary</option>
                    <option value="Secondary">Secondary</option>
                    <option value="Sub-Category">Sub-Category</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="categoryStatus" className="form-label">
                    Status
                  </label>
                  <select
                    id="categoryStatus"
                    value={formData.categoryStatus}
                    onChange={(e) => handleInputChange('categoryStatus', e.target.value)}
                    className="form-input"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Suspended">Suspended</option>
                  </select>
                </div>
              </div>

              {/* Row 2: Category Description */}
              <div className="form-group">
                <label htmlFor="categoryDescription" className="form-label required">
                  Category Description
                </label>
                <textarea
                  id="categoryDescription"
                  value={formData.categoryDescription}
                  onChange={(e) => handleInputChange('categoryDescription', e.target.value)}
                  className="form-input"
                  placeholder="Describe the purpose and scope of this project category..."
                  rows={3}
                  maxLength={500}
                />
              </div>

              {/* Row 3: Parent Category, Color, Icon, Sort Order */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="parentCategory" className="form-label">
                    Parent Category
                  </label>
                  <select
                    id="parentCategory"
                    value={formData.parentCategory}
                    onChange={(e) => handleInputChange('parentCategory', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Parent Category</option>
                    {parentCategories.map(cat => (
                      <option key={cat._id} value={cat._id}>
                        {cat.categoryName} ({cat.categoryCode})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="categoryColor" className="form-label">
                    Category Color
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      id="categoryColor"
                      type="color"
                      value={formData.categoryColor}
                      onChange={(e) => handleInputChange('categoryColor', e.target.value)}
                      className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.categoryColor}
                      onChange={(e) => handleInputChange('categoryColor', e.target.value)}
                      className="form-input flex-1"
                      placeholder="#222448"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="categoryIcon" className="form-label">
                    Category Icon
                  </label>
                  <select
                    id="categoryIcon"
                    value={formData.categoryIcon}
                    onChange={(e) => handleInputChange('categoryIcon', e.target.value)}
                    className="form-input"
                  >
                    {categoryIcons.map(icon => (
                      <option key={icon} value={icon}>
                        {icon.charAt(0).toUpperCase() + icon.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="sortOrder" className="form-label">
                    Sort Order
                  </label>
                  <input
                    id="sortOrder"
                    type="number"
                    value={formData.sortOrder}
                    onChange={(e) => handleInputChange('sortOrder', parseInt(e.target.value) || 0)}
                    className="form-input"
                    min="0"
                  />
                </div>
              </div>

              {/* Row 4: Applicable Project Types */}
              <div className="form-group">
                <label className="form-label">Applicable Project Types</label>
                <div className="checkbox-group" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {projectTypes.map(option => (
                    <label key={option.value} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={formData.applicableProjectTypes.includes(option.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            addArrayItem('applicableProjectTypes', option.value)
                          } else {
                            removeArrayItem('applicableProjectTypes', formData.applicableProjectTypes.indexOf(option.value))
                          }
                        }}
                        style={{ margin: 0 }}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Budget & Timeline Tab */}
          {activeTab === 'budget' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Budget & Timeline Settings</h3>
              </div>

              {/* Budget Allocation */}
              <div className="form-group">
                <label className="form-label">Budget Allocation</label>
                <div className="grid-row-4">
                  <div className="form-group">
                    <label htmlFor="minBudget" className="form-label">
                      Minimum Budget
                    </label>
                    <input
                      id="minBudget"
                      type="number"
                      value={formData.budgetAllocation.minBudget}
                      onChange={(e) => handleInputChange('budgetAllocation.minBudget', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="maxBudget" className="form-label">
                      Maximum Budget
                    </label>
                    <input
                      id="maxBudget"
                      type="number"
                      value={formData.budgetAllocation.maxBudget}
                      onChange={(e) => handleInputChange('budgetAllocation.maxBudget', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="defaultBudget" className="form-label">
                      Default Budget
                    </label>
                    <input
                      id="defaultBudget"
                      type="number"
                      value={formData.budgetAllocation.defaultBudget}
                      onChange={(e) => handleInputChange('budgetAllocation.defaultBudget', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="currency" className="form-label">
                      Currency
                    </label>
                    <select
                      id="currency"
                      value={formData.budgetAllocation.currency}
                      onChange={(e) => handleInputChange('budgetAllocation.currency', e.target.value)}
                      className="form-input"
                    >
                      {currencies.map(currency => (
                        <option key={currency} value={currency}>{currency}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Timeline Settings */}
              <div className="form-group">
                <label className="form-label">Timeline Settings</label>
                <div className="grid-row-4">
                  <div className="form-group">
                    <label htmlFor="defaultDuration" className="form-label">
                      Default Duration
                    </label>
                    <input
                      id="defaultDuration"
                      type="number"
                      value={formData.timelineSettings.defaultDuration}
                      onChange={(e) => handleInputChange('timelineSettings.defaultDuration', parseInt(e.target.value) || 0)}
                      className="form-input"
                      min="1"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="durationUnit" className="form-label">
                      Duration Unit
                    </label>
                    <select
                      id="durationUnit"
                      value={formData.timelineSettings.durationUnit}
                      onChange={(e) => handleInputChange('timelineSettings.durationUnit', e.target.value)}
                      className="form-input"
                    >
                      {durationUnits.map(unit => (
                        <option key={unit} value={unit}>{unit}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={formData.timelineSettings.allowExtension}
                        onChange={(e) => handleInputChange('timelineSettings.allowExtension', e.target.checked)}
                        style={{ margin: 0 }}
                      />
                      <span>Allow Extension</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label htmlFor="maxExtensionDays" className="form-label">
                      Max Extension Days
                    </label>
                    <input
                      id="maxExtensionDays"
                      type="number"
                      value={formData.timelineSettings.maxExtensionDays}
                      onChange={(e) => handleInputChange('timelineSettings.maxExtensionDays', parseInt(e.target.value) || 0)}
                      className="form-input"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Resources & Quality Tab */}
          {activeTab === 'resources' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Resource Requirements & Quality Standards</h3>
              </div>

              {/* Resource Requirements */}
              <div className="form-group">
                <label className="form-label">Resource Requirements</label>
                <div className="grid-row-4">
                  <div className="form-group">
                    <label htmlFor="minTeamSize" className="form-label">
                      Minimum Team Size
                    </label>
                    <input
                      id="minTeamSize"
                      type="number"
                      value={formData.resourceRequirements.minTeamSize}
                      onChange={(e) => handleInputChange('resourceRequirements.minTeamSize', parseInt(e.target.value) || 1)}
                      className="form-input"
                      min="1"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="maxTeamSize" className="form-label">
                      Maximum Team Size
                    </label>
                    <input
                      id="maxTeamSize"
                      type="number"
                      value={formData.resourceRequirements.maxTeamSize}
                      onChange={(e) => handleInputChange('resourceRequirements.maxTeamSize', parseInt(e.target.value) || 1)}
                      className="form-input"
                      min="1"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Required Skills</label>
                    <div className="array-input-group">
                      {formData.resourceRequirements.requiredSkills.map((skill, index) => (
                        <div key={index} className="array-item">
                          <input
                            type="text"
                            value={skill}
                            onChange={(e) => {
                              const newSkills = [...formData.resourceRequirements.requiredSkills]
                              newSkills[index] = e.target.value
                              handleInputChange('resourceRequirements.requiredSkills', newSkills)
                            }}
                            className="form-input"
                            placeholder="e.g., JavaScript, React, Node.js"
                          />
                          <button
                            type="button"
                            onClick={() => removeArrayItem('resourceRequirements.requiredSkills', index)}
                            className="btn-danger-small"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addArrayItem('resourceRequirements.requiredSkills', '')}
                        className="btn-secondary-small"
                      >
                        Add Skill
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Preferred Roles</label>
                    <div className="array-input-group">
                      {formData.resourceRequirements.preferredRoles.map((role, index) => (
                        <div key={index} className="array-item">
                          <input
                            type="text"
                            value={role}
                            onChange={(e) => {
                              const newRoles = [...formData.resourceRequirements.preferredRoles]
                              newRoles[index] = e.target.value
                              handleInputChange('resourceRequirements.preferredRoles', newRoles)
                            }}
                            className="form-input"
                            placeholder="e.g., Developer, Designer, QA"
                          />
                          <button
                            type="button"
                            onClick={() => removeArrayItem('resourceRequirements.preferredRoles', index)}
                            className="btn-danger-small"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addArrayItem('resourceRequirements.preferredRoles', '')}
                        className="btn-secondary-small"
                      >
                        Add Role
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quality Standards */}
              <div className="form-group">
                <label className="form-label">Quality Standards</label>
                <div className="grid-row-4">
                  <div className="form-group">
                    <label htmlFor="qualityLevel" className="form-label">
                      Quality Level
                    </label>
                    <select
                      id="qualityLevel"
                      value={formData.qualityStandards.qualityLevel}
                      onChange={(e) => handleInputChange('qualityStandards.qualityLevel', e.target.value)}
                      className="form-input"
                    >
                      {qualityLevels.map(level => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={formData.qualityStandards.requiresTesting}
                        onChange={(e) => handleInputChange('qualityStandards.requiresTesting', e.target.checked)}
                        style={{ margin: 0 }}
                      />
                      <span>Requires Testing</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={formData.qualityStandards.requiresDocumentation}
                        onChange={(e) => handleInputChange('qualityStandards.requiresDocumentation', e.target.checked)}
                        style={{ margin: 0 }}
                      />
                      <span>Requires Documentation</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={formData.qualityStandards.requiresCodeReview}
                        onChange={(e) => handleInputChange('qualityStandards.requiresCodeReview', e.target.checked)}
                        style={{ margin: 0 }}
                      />
                      <span>Requires Code Review</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Compliance Requirements */}
              <div className="form-group">
                <label className="form-label">Compliance Requirements</label>
                <div className="array-input-group">
                  {formData.qualityStandards.complianceRequirements.map((requirement, index) => (
                    <div key={index} className="array-item">
                      <input
                        type="text"
                        value={requirement}
                        onChange={(e) => {
                          const newRequirements = [...formData.qualityStandards.complianceRequirements]
                          newRequirements[index] = e.target.value
                          handleInputChange('qualityStandards.complianceRequirements', newRequirements)
                        }}
                        className="form-input"
                        placeholder="e.g., GDPR, HIPAA, PCI DSS"
                      />
                      <button
                        type="button"
                        onClick={() => removeArrayItem('qualityStandards.complianceRequirements', index)}
                        className="btn-danger-small"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayItem('qualityStandards.complianceRequirements', '')}
                    className="btn-secondary-small"
                  >
                    Add Requirement
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Category Settings</h3>
              </div>

              {/* Row 1: Approval Settings */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.requiresApproval}
                      onChange={(e) => handleInputChange('requiresApproval', e.target.checked)}
                      style={{ margin: 0 }}
                    />
                    <span>Requires Approval</span>
                  </label>
                </div>

                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.isDefault}
                      onChange={(e) => handleInputChange('isDefault', e.target.checked)}
                      style={{ margin: 0 }}
                    />
                    <span>Set as Default Category</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={handleReset}
            >
              <RotateCcw size={18} />
              Reset
            </button>
            
            <button 
              type="button" 
              className="btn-primary" 
              onClick={handleSave}
              disabled={loading}
            >
              <Save size={18} />
              {loading ? 'Creating...' : 'Create Category'}
            </button>
          </div>
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Project Category Master – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default ProjectCategoryMasterForm
